import type { PeriodValue as LumenPeriodValue } from '@shinetools/lumen-react'
import type { Locale } from 'date-fns'
import type { TFunction } from 'i18next'

import { formatCustomRangeTriggerLabel } from './formatCustomRangeTriggerLabel'
import { formatDefaultQuarterTriggerLabel } from './formatDefaultQuarterTriggerLabel'

interface FormatPeriodTriggerLabelProps {
  formatFiscalYearLabel?: (year: number) => string | undefined
  formatQuarterLabel?: (quarter: number, year: number) => string | undefined
  locale: Locale
  monthLabels: readonly string[]
  t: TFunction
  value: LumenPeriodValue
}

/**
 * Builds a `PeriodPicker` trigger label for the month, quarter, fiscal-year and custom-range modes, falling back
 * to `undefined` (Lumen's own formatting) for any other mode, e.g. `week` when a consumer excludes it via
 * `availableModes` but it stays part of Lumen's `PeriodValue` union.
 *
 * Quarter and fiscal-year default to the generic `period_select.trigger_label.*` copy. Pass `formatQuarterLabel`
 * / `formatFiscalYearLabel` only when a consumer needs different wording for these modes.
 */
export const formatPeriodTriggerLabel = (props: FormatPeriodTriggerLabelProps): string | undefined => {
  const { formatFiscalYearLabel, formatQuarterLabel, locale, monthLabels, t, value } = props

  switch (value.mode) {
    case 'month':
      return `${monthLabels[value.month]} ${value.year}`

    case 'quarter':
      return formatQuarterLabel
        ? formatQuarterLabel(value.quarter, value.year)
        : formatDefaultQuarterTriggerLabel(value.quarter, value.year, t)

    case 'fiscalYear':
      return formatFiscalYearLabel
        ? formatFiscalYearLabel(value.year)
        : t('period_select.trigger_label.fiscal_year', { year: value.year })

    case 'custom':
      return formatCustomRangeTriggerLabel(value.start, value.end, locale)

    default:
      return undefined
  }
}
